import { ExtendedMap, isArrayNotEmpty, type NullableValues, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../../utils'

import { Capability, CybusPermissionContext, type CybusPersistedPermission } from '../../domain'

import type { BackendDefinition } from '.'

export type AuthenticatedResource<Path extends CybusPersistedPermission['resource']> = Readonly<{
    /**
     * The path template of the url
     */
    path: Path

    /**
     * The associated capability of the request
     * Nullable if there shouldn't be one
     */
    capability: Capability | null
}>

/**
 * This represents a map of a permission requirement
 * For every Admin UI permission, a bunch of permission requirements need to be fulfilled
 *
 * Some do not require a specific resource (so it is nullable)
 */
export type PermissionRequirement = Pick<CybusPersistedPermission, 'context' | 'read' | 'write'> & NullableValues<Pick<CybusPersistedPermission, 'resource'>>

type ExclusivePermissionRequirement<T extends CybusPermissionContext> = PermissionRequirement & Readonly<{ context: T }>

const httpCapabilities: ReadonlyRecord<Capability, readonly ExclusivePermissionRequirement<CybusPermissionContext.HTTP>[]> = {
    [Capability.AGENTS_MANAGE]: [{ resource: '/api/resources/runners/+', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.AGENTS_READ]: [{ resource: '/api/v2/resource-runners', context: CybusPermissionContext.HTTP, read: true, write: false }],

    [Capability.CONNECTION_READ]: [
        { resource: '/api/v2/resources/+', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.CONNECTIONS_MANAGE]: [{ resource: '/api/v2/resources/+/operation', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.CONNECTIONS_READ]: [
        { resource: '/api/v2/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/count', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services/+/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.CORE_CONTAINER_READ]: [{ resource: '/api/core-containers/+/inspect', context: CybusPermissionContext.HTTP, read: true, write: false }],
    [Capability.CORE_CONTAINERS_MANAGE]: [{ resource: '/api/core-containers/+/operation', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.CORE_CONTAINERS_READ]: [
        { resource: '/api/core-containers', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/core-containers/+/inspect', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/core-containers/orchestrator', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.SERVICE_CONTAINER_READ]: [{ resource: '/api/containers/+/inspect', context: CybusPermissionContext.HTTP, read: true, write: false }],
    [Capability.SERVICE_CONTAINERS_MANAGE]: [{ resource: '/api/containers/+/operation', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.SERVICE_CONTAINERS_READ]: [
        { resource: '/api/containers', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/containers/+/inspect', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/containers/orchestrator', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.ENDPOINT_READ]: [
        { resource: '/api/v2/resources/+', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.ENDPOINTS_MANAGE]: [{ resource: '/api/v2/resources/+/operation', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.ENDPOINTS_READ]: [
        { resource: '/api/v2/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/count', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services/+/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.MAPPING_READ]: [
        { resource: '/api/v2/resources/+', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.MAPPINGS_MANAGE]: [{ resource: '/api/v2/resources/+/operation', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.MAPPINGS_READ]: [
        { resource: '/api/v2/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/count', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services/+/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.NODES_READ]: [
        { resource: '/api/v2/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/count', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services/+/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.SERVER_READ]: [
        { resource: '/api/v2/resources/+', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.SERVERS_MANAGE]: [{ resource: '/api/v2/resources/+/operation', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.SERVERS_READ]: [
        { resource: '/api/v2/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/count', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services/+/resources', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.SERVICE_READ]: [
        { resource: '/api/services/+/dependencies', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/services/all-links', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/count', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services/+', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.SERVICE_COMMISSIONING_FILE_READ]: [
        { resource: '/api/v2/services/+/serviceCommissioningFile', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.SERVICES_MANAGE]: [
        { resource: '/api/services/+', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/services/+/operation', context: CybusPermissionContext.HTTP, read: false, write: true },
    ],
    [Capability.SERVICES_PARAMETERS_READ]: [{ resource: '/api/services/parametersSchema', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.SERVICES_CREATE_OR_UPDATE]: [
        { resource: '/api/services', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/v2/services/+', context: CybusPermissionContext.HTTP, read: false, write: true },
    ],
    [Capability.SERVICES_READ]: [
        { resource: '/api/services/all-links', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/count', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services/stats/count', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.SERVICES_CATALOG_READ]: [
        { resource: '/api/marketplace/app', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/marketplace/app/meta', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/marketplace/apps', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.DEVIATIONS_READ]: [
        { resource: '/api/v2/resources/states/deviations', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/count', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/states/deviations/services', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/services/+', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.SERVICE_SUBSCRIPTION_METADATA]: [{ resource: '/api/v2/services/+/resources', context: CybusPermissionContext.HTTP, read: true, write: false }],

    [Capability.VOLUME_READ]: [{ resource: '/api/volumes/+/inspect', context: CybusPermissionContext.HTTP, read: true, write: false }],
    [Capability.VOLUMES_MANAGE]: [],
    [Capability.VOLUMES_READ]: [{ resource: '/api/volumes', context: CybusPermissionContext.HTTP, read: true, write: false }],

    [Capability.RETRIEVE_PASSWORD_POLICY]: [{ resource: '/api/policy/password', context: CybusPermissionContext.HTTP, read: true, write: false }],
    [Capability.RETRIEVE_USERNAME_POLICY]: [{ resource: '/api/policy/username', context: CybusPermissionContext.HTTP, read: true, write: false }],

    [Capability.MANAGE_USERS]: [
        { resource: '/api/listUsers', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/users/+', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/users/batch', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/users/batch/delete', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/users/usernames', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.MANAGE_ROLES]: [
        { resource: '/api/roles', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/roles/+', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/roles/names', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/roles/page', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.USER_MANAGEMENT_CONFIGURATION]: [
        { resource: '/api/auth/ldap', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/auth/mfa', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.MANAGE_PERMISSIONS]: [
        { resource: '/api/permissions/ids', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/permissions/page', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.CLIENT_REGISTRY_MANAGE]: [
        { resource: '/api/client-registry', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/client-registry/confirm', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/client-registry/lock', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/client-registry/open', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/client-registry/status', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.CERTIFICATES_MANAGE]: [
        { resource: '/api/certificates', context: CybusPermissionContext.HTTP, read: true, write: true },
        { resource: '/api/certificates/content', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.TOPICS_SUBSCRIPTION]: [],
    [Capability.TOPIC_CHUNKS_READ]: [{ resource: '/api/v2/mqtt/topics/levels', context: CybusPermissionContext.HTTP, read: true, write: false }],
    [Capability.RULE_ENGINE_RESOURCES]: [{ resource: '/api/v2/resources', context: CybusPermissionContext.HTTP, read: true, write: false }],
    [Capability.WORKBENCH]: [],

    [Capability.LOGS_READ]: [
        { resource: '/api/core-containers', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/core-containers/+/logs', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/core-containers/orchestrator', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/v2/resources/+', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],

    [Capability.SYSTEM_LICENSE_MANAGE]: [
        { resource: '/api/system/licensefile', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/system/refresh', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.MANAGE_OWN_PASSWORD]: [{ resource: '/api/users/change-password', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.MINIMUM]: [
        { resource: '/api/system/info', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/system/preflight', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.SYSTEM_METRICS_MANAGE]: [{ resource: '/api/system/metrics', context: CybusPermissionContext.HTTP, read: false, write: true }],
    [Capability.SYSTEM_METRICS_READ]: [
        { resource: '/api/system/metrics', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/system/preflight', context: CybusPermissionContext.HTTP, read: true, write: false },
    ],
    [Capability.USE_MFA]: [
        { resource: '/api/mfa/disable', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/mfa/enable', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/mfa/isenrolled', context: CybusPermissionContext.HTTP, read: true, write: false },
        { resource: '/api/mfa/regenerate/backupcodes', context: CybusPermissionContext.HTTP, read: false, write: true },
        { resource: '/api/mfa/validate', context: CybusPermissionContext.HTTP, read: false, write: true },
    ],
}

const mqttCapabilities: ReadonlyRecord<Capability, readonly ExclusivePermissionRequirement<CybusPermissionContext.MQTT>[]> = {
    [Capability.AGENTS_MANAGE]: [],
    [Capability.AGENTS_READ]: [],

    [Capability.CONNECTION_READ]: [],
    [Capability.CONNECTIONS_MANAGE]: [],
    [Capability.CONNECTIONS_READ]: [],

    [Capability.CORE_CONTAINER_READ]: [],
    [Capability.CORE_CONTAINERS_MANAGE]: [],
    [Capability.CORE_CONTAINERS_READ]: [],

    [Capability.SERVICE_CONTAINER_READ]: [],
    [Capability.SERVICE_CONTAINERS_MANAGE]: [],
    [Capability.SERVICE_CONTAINERS_READ]: [],

    [Capability.ENDPOINT_READ]: [],
    [Capability.ENDPOINTS_MANAGE]: [],
    [Capability.ENDPOINTS_READ]: [],

    [Capability.MAPPING_READ]: [],
    [Capability.MAPPINGS_MANAGE]: [],
    [Capability.MAPPINGS_READ]: [],

    [Capability.NODES_READ]: [],

    [Capability.SERVER_READ]: [],
    [Capability.SERVERS_MANAGE]: [],
    [Capability.SERVERS_READ]: [],

    [Capability.SERVICE_READ]: [],
    [Capability.SERVICES_MANAGE]: [],
    [Capability.SERVICE_COMMISSIONING_FILE_READ]: [],
    [Capability.SERVICES_PARAMETERS_READ]: [],
    [Capability.SERVICES_CREATE_OR_UPDATE]: [],
    [Capability.SERVICES_READ]: [],
    [Capability.SERVICES_CATALOG_READ]: [],
    [Capability.DEVIATIONS_READ]: [],
    [Capability.SERVICE_SUBSCRIPTION_METADATA]: [],

    [Capability.VOLUME_READ]: [],
    [Capability.VOLUMES_MANAGE]: [],
    [Capability.VOLUMES_READ]: [],

    [Capability.RETRIEVE_PASSWORD_POLICY]: [],
    [Capability.RETRIEVE_USERNAME_POLICY]: [],
    [Capability.MANAGE_USERS]: [],
    [Capability.MANAGE_ROLES]: [],
    [Capability.MANAGE_PERMISSIONS]: [],
    [Capability.CLIENT_REGISTRY_MANAGE]: [],
    [Capability.CERTIFICATES_MANAGE]: [],
    [Capability.USER_MANAGEMENT_CONFIGURATION]: [],

    [Capability.TOPICS_SUBSCRIPTION]: [{ resource: null, context: CybusPermissionContext.MQTT, read: true, write: false }],
    [Capability.TOPIC_CHUNKS_READ]: [],
    [Capability.RULE_ENGINE_RESOURCES]: [],
    [Capability.WORKBENCH]: [],

    [Capability.LOGS_READ]: [],

    [Capability.SYSTEM_LICENSE_MANAGE]: [],
    [Capability.MINIMUM]: [],
    [Capability.SYSTEM_METRICS_MANAGE]: [],
    [Capability.SYSTEM_METRICS_READ]: [],
    [Capability.USE_MFA]: [],

    [Capability.MANAGE_OWN_PASSWORD]: [],
}

const externalhttp: ReadonlyRecord<Capability, readonly ExclusivePermissionRequirement<CybusPermissionContext.HTTP>[]> = {
    [Capability.AGENTS_MANAGE]: [],
    [Capability.AGENTS_READ]: [],

    [Capability.CONNECTION_READ]: [],
    [Capability.CONNECTIONS_MANAGE]: [],
    [Capability.CONNECTIONS_READ]: [],

    [Capability.CORE_CONTAINER_READ]: [],
    [Capability.CORE_CONTAINERS_MANAGE]: [],
    [Capability.CORE_CONTAINERS_READ]: [],

    [Capability.SERVICE_CONTAINER_READ]: [],
    [Capability.SERVICE_CONTAINERS_MANAGE]: [],
    [Capability.SERVICE_CONTAINERS_READ]: [],

    [Capability.ENDPOINT_READ]: [],
    [Capability.ENDPOINTS_MANAGE]: [],
    [Capability.ENDPOINTS_READ]: [],

    [Capability.MAPPING_READ]: [],
    [Capability.MAPPINGS_MANAGE]: [],
    [Capability.MAPPINGS_READ]: [],

    [Capability.NODES_READ]: [],

    [Capability.SERVER_READ]: [],
    [Capability.SERVERS_MANAGE]: [],
    [Capability.SERVERS_READ]: [],

    [Capability.SERVICE_READ]: [],
    [Capability.SERVICE_COMMISSIONING_FILE_READ]: [],
    [Capability.SERVICES_MANAGE]: [],
    [Capability.SERVICES_PARAMETERS_READ]: [],
    [Capability.SERVICES_CREATE_OR_UPDATE]: [],
    [Capability.SERVICES_READ]: [],
    [Capability.SERVICES_CATALOG_READ]: [],
    [Capability.DEVIATIONS_READ]: [],
    [Capability.SERVICE_SUBSCRIPTION_METADATA]: [],

    [Capability.VOLUME_READ]: [],
    [Capability.VOLUMES_MANAGE]: [],
    [Capability.VOLUMES_READ]: [],

    [Capability.RETRIEVE_PASSWORD_POLICY]: [],
    [Capability.RETRIEVE_USERNAME_POLICY]: [],
    [Capability.MANAGE_USERS]: [],
    [Capability.MANAGE_ROLES]: [],
    [Capability.MANAGE_PERMISSIONS]: [],
    [Capability.CLIENT_REGISTRY_MANAGE]: [],
    [Capability.CERTIFICATES_MANAGE]: [],
    [Capability.USER_MANAGEMENT_CONFIGURATION]: [],

    [Capability.TOPICS_SUBSCRIPTION]: [],
    [Capability.TOPIC_CHUNKS_READ]: [],
    [Capability.RULE_ENGINE_RESOURCES]: [],
    [Capability.WORKBENCH]: [
        { resource: '/workbench', context: CybusPermissionContext.HTTP, read: true, write: true },
        { resource: '/workbench/#', context: CybusPermissionContext.HTTP, read: true, write: true },
    ],

    [Capability.LOGS_READ]: [],

    [Capability.SYSTEM_LICENSE_MANAGE]: [],
    [Capability.MINIMUM]: [],
    [Capability.SYSTEM_METRICS_MANAGE]: [],
    [Capability.SYSTEM_METRICS_READ]: [],
    [Capability.USE_MFA]: [],

    [Capability.MANAGE_OWN_PASSWORD]: [],
}

export const capabilities = Object.values(Capability).reduce(
    (r, capability) => ({
        ...r,
        /** Only keep alternatives that actually have permission requirements */
        [capability]: [httpCapabilities, externalhttp, mqttCapabilities].flatMap(({ [capability]: alternative }) =>
            isArrayNotEmpty(alternative) ? [alternative] : []
        ),
    }),
    {} as ReadonlyRecord<Capability, ReadonlyNonEmptyArray<ReadonlyNonEmptyArray<PermissionRequirement>>>
)

/**
 * Having Kubernetes or Docker in the backend result in different Connectware capabililities
 * This are the permissions that are missing on each
 */
export const unavailableByContainerImplementation = new ExtendedMap<BackendDefinition<'container-manager', 'Orchestrator'> | null, Capability[]>([
    [
        'Kubernetes',
        [
            /** No capability to interact with volumes */
            Capability.VOLUMES_MANAGE,
            Capability.VOLUMES_READ,
            Capability.VOLUME_READ,
            /** No capability to interact with service containers */
            Capability.SERVICE_CONTAINERS_MANAGE,
            Capability.SERVICE_CONTAINERS_READ,
            Capability.SERVICE_CONTAINER_READ,
            /** No capability to manage core containers */
            Capability.CORE_CONTAINERS_MANAGE,
        ],
    ],
    ['Docker', []],
])

/**
 * If MFA is disabled (false) then remove the following values
 */
export const unavailableMfaConfiguration = new ExtendedMap([
    [false, [Capability.USE_MFA]],
    [true, []],
])
